import * as apiTypes from '@knots/api-sdk/dist/apiSchema'
import * as codeAppApiTypes from '@knots/studio-sdk/dist/apiSchema'
import { studioApiSdk, knotsApiSdk } from './fetcher'

const getUserProject = async (projectId?: number): Promise<StudioProject> => {
  if (projectId) {
    return await getProject(projectId)
  }

  const projects = await getProjects()

  if (projects.length) {
    return projects[0]
  }

  return await createProject(`Main project ${Math.floor(Math.random() * 10000)}`, 'Main project for all routes')
}

/**
 * Function that creates route and handles project creation if needed
 * @param projectId
 * @param additionalRouteData
 * @return routeUrl - Url that can be used for redirect
 */
export const startRoute = async (
  projectId?: number,
  additionalRouteData: Partial<codeAppApiTypes.components['schemas']['StudioRoute']> = {}
): Promise<string> => {
  const project = await getUserProject(projectId)
  const routeData = {
    name: 'New route',
    description: 'New workspace route',
    runtime: 'node22',
    ...additionalRouteData,
  } as StudioRoute

  const route = await createRoute({ route: routeData, code: '', projectId: project.id as number })
  return `/projects/${project.id}/${route.id}`
}

// PROJECTS
export type StudioProject = codeAppApiTypes.components['schemas']['StudioProject']
export const getProjects = async (): Promise<StudioProject[]> => {
  return studioApiSdk.getProjects()
}

export const getProject = async (id: number): Promise<StudioProject> => {
  return studioApiSdk.getProject(id)
}

export const createProject = async (name: string, description: string): Promise<StudioProject> => {
  return studioApiSdk.createProject(name, description)
}

export const updateProject = async (id: number, name: string, description: string): Promise<StudioProject> => {
  return studioApiSdk.updateProject(id, name, description)
}

// ROUTES
export type StudioRoute = codeAppApiTypes.components['schemas']['StudioRoute']
export type CodeAppConfig = codeAppApiTypes.components['schemas']['CodeAppConfig']
export type CodeAppConfigType = codeAppApiTypes.components['schemas']['CodeAppConfigType']
export type CodeAppRouteHistory = codeAppApiTypes.components['schemas']['CodeAppRouteHistory']

export const getRoutes = async (projectId: number): Promise<StudioRoute[]> => {
  return studioApiSdk.getRoutes(projectId)
}

export const getRoute = async (
  routeId: number,
  projectId: number
): Promise<{ route: StudioRoute; code: string; config: CodeAppConfig }> => {
  return studioApiSdk.getRoute(routeId, projectId)
}

export type CreateRouteArgs = {
  route: StudioRoute
  code?: string
  projectId: number
  templateId?: number
  requirements?: {
    connectors?: Record<string, string>
    secrets?: Record<string, string>
    params?: Record<string, string>
  }
}

export const createRoute = async ({
  route,
  code = '',
  projectId,
  templateId,
  requirements,
}: CreateRouteArgs): Promise<StudioRoute> => {
  return studioApiSdk.createRoute(route, projectId, templateId, code, requirements)
}

export const updateRoute = async (
  projectId: number,
  routeId: number,
  route: Partial<StudioRoute>,
  code?: string | null
): Promise<StudioRoute> => {
  return studioApiSdk.updateRoute(routeId, projectId, route, code)
}

export const deleteRoute = async (routeId: number, projectId: number): Promise<void> => {
  return studioApiSdk.deleteRoute(routeId, projectId)
}

export const getRouteHistoryList = async (
  routeId: number,
  projectId: number,
  startFrom: number | null = null
): Promise<CodeAppRouteHistory[]> => {
  return studioApiSdk.getRouteHistoryList(routeId, projectId, startFrom || undefined)
}

export type Module = apiTypes.components['schemas']['Module']
export type TrialInfo = {
  isInfoComplete: boolean
  trialEnd: string | null
}
export type TrialInfoFilterData = apiTypes.paths['/billingEntities/trial-info']['get']['parameters']['query']

export type GetClientFilterData = apiTypes.paths['/clients']['get']['parameters']['query']
export type Client = apiTypes.components['schemas']['Client']

export const getClients = async (filter: GetClientFilterData): Promise<Client[]> => {
  return knotsApiSdk.getClients(filter)
}

export const getModules = async (): Promise<Module[]> => {
  return knotsApiSdk.listModules()
}
export const getTrialInfo = async (filters: TrialInfoFilterData = {}): Promise<TrialInfo> => {
  return knotsApiSdk.getTrialInfo(filters)
}

export type OAuth2TokenResponse = apiTypes.components['schemas']['OAuth2TokenResponse']

export const getOAuth2Token = async (): Promise<OAuth2TokenResponse> => {
  return knotsApiSdk.getSSOToken()
}

export type RouteTemplate = codeAppApiTypes.components['schemas']['StudioRouteTemplate']

export const getRouteTypes = async (routeType: StudioRoute['type']): Promise<{ types: CodeAppConfigType[] }> => {
  return studioApiSdk.getRouteTypes(routeType)
}
